/**
 * Auth state management — token storage, login/logout, token refresh.
 * Mirrors apps/admin-ui/src/lib/auth.ts, with localStorage keys namespaced to
 * `cluster_workspace_*` so the workspace never shares credentials with other
 * cluster apps running on the same origin family.
 *
 * The session user is derived by decoding the RS256 access token's claims
 * (`sub` / `email` / `role`) — `POST /auth/login` returns no user object, so
 * there is deliberately no `/auth/me` round-trip (design §7).
 */

import type { LoginRequest, LoginResponse, RefreshResponse, User } from '@/types';

import { apiClient } from './api';

const TOKEN_KEY = 'cluster_workspace_access_token';
const REFRESH_KEY = 'cluster_workspace_refresh_token';
const USER_KEY = 'cluster_workspace_user';
/**
 * Flag set right before a forced logout (failed refresh). The login page shows
 * it once — the app has no toast provider yet (design §13.1).
 */
const SESSION_EXPIRED_KEY = 'cluster_workspace_session_expired';

// ── Token helpers ───────────────────────────────────────────────────────────

export function getToken(): string | null {
  return localStorage.getItem(TOKEN_KEY);
}

export function getRefreshToken(): string | null {
  return localStorage.getItem(REFRESH_KEY);
}

export function getUser(): User | null {
  const raw = localStorage.getItem(USER_KEY);
  if (!raw) {
    return null;
  }
  try {
    return JSON.parse(raw) as User;
  } catch {
    return null;
  }
}

export function isAuthenticated(): boolean {
  return !!getToken();
}

export interface AccessTokenClaims {
  sub?: string;
  email?: string;
  role?: string;
  exp?: number;
  iat?: number;
}

/**
 * Decode the access-token payload **without** verifying the signature. This is
 * display/hydration only — authorization stays server-side, so an unverified
 * claim is never a security decision here.
 */
export function decodeAccessToken(token: string): AccessTokenClaims | null {
  const parts = token.split('.');
  if (parts.length !== 3) {
    return null;
  }
  try {
    const payload = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    return JSON.parse(atob(payload)) as AccessTokenClaims;
  } catch {
    return null;
  }
}

/**
 * Persist the session user derived from the access token (`sub` → `_id`).
 * Returns `null` when the token carries no subject claim.
 */
export function persistUserFromToken(token: string): User | null {
  const claims = decodeAccessToken(token);
  if (!claims?.sub) {
    return null;
  }

  const user: User = { _id: claims.sub, id: claims.sub };
  if (claims.email) {
    user.email = claims.email;
  }
  if (claims.role) {
    user.role = claims.role;
  }
  localStorage.setItem(USER_KEY, JSON.stringify(user));
  return user;
}

/**
 * Check if the current JWT token is expiring soon.
 * @param thresholdSeconds - How many seconds before expiration to consider "expiring soon"
 * @returns true if token expires within thresholdSeconds, or if token is invalid/missing
 */
export function isTokenExpiringSoon(thresholdSeconds = 60): boolean {
  const token = getToken();
  if (!token) {
    return true;
  }

  const claims = decodeAccessToken(token);
  if (!claims?.exp) {
    return true;
  }

  const now = Math.floor(Date.now() / 1000);
  return claims.exp - now < thresholdSeconds;
}

/**
 * Reactive expiry safety net (mirrors admin-ui's `isTokenExpiringSoon` boot
 * check): refresh proactively when the access token is about to die, so the
 * first authenticated request never rides an expired token.
 *
 * A failed refresh clears local auth — the rotating refresh-token family may
 * have been revoked, so the session is gone.
 */
export async function ensureFreshSession(): Promise<User | null> {
  const token = getToken();
  if (!token) {
    return null;
  }

  if (isTokenExpiringSoon(60) && getRefreshToken()) {
    try {
      await refreshToken();
    } catch {
      clearAuth();
      markSessionExpired();
      return null;
    }
  }

  return getUser() ?? persistUserFromToken(token);
}

// ── Session-expiry notice ───────────────────────────────────────────────────

/**
 * Flags that the session ended involuntarily. Read once at store creation
 * (`features/auth/authSlice.ts` → `readInitialState`) so the login page can
 * explain itself even when the logout arrived as a hard redirect.
 */
export function markSessionExpired(): void {
  sessionStorage.setItem(SESSION_EXPIRED_KEY, 'true');
}

/** Reads and clears the flag. */
export function takeSessionExpiredNotice(): boolean {
  const marked = sessionStorage.getItem(SESSION_EXPIRED_KEY) === 'true';
  if (marked) {
    sessionStorage.removeItem(SESSION_EXPIRED_KEY);
  }
  return marked;
}

function clearSessionExpiredNotice(): void {
  sessionStorage.removeItem(SESSION_EXPIRED_KEY);
}

// ── Auth actions ────────────────────────────────────────────────────────────

export async function login(credentials: LoginRequest): Promise<LoginResponse> {
  const { data } = await apiClient.post<LoginResponse>('/auth/login', credentials);
  setTokens(data.accessToken, data.refreshToken);
  persistUserFromToken(data.accessToken);
  clearSessionExpiredNotice();
  return data;
}

/**
 * In-flight refresh, shared by every caller. Refresh tokens rotate on each use
 * and the service revokes the whole family on replay (`refreshToken.service.ts`
 * replay detection), so two concurrent refreshes would kill the session —
 * React StrictMode's double effect and parallel 401s must reuse one request.
 */
let inflightRefresh: Promise<RefreshResponse> | null = null;

export function refreshToken(): Promise<RefreshResponse> {
  if (inflightRefresh) {
    return inflightRefresh;
  }

  const currentRefresh = getRefreshToken();
  if (!currentRefresh) {
    return Promise.reject(new Error('No refresh token'));
  }

  inflightRefresh = (async () => {
    try {
      const { data } = await apiClient.post<RefreshResponse>('/auth/refresh', {
        refreshToken: currentRefresh,
      });
      // Rotating family: the new refresh token replaces the old one immediately.
      setTokens(data.accessToken, data.refreshToken);
      persistUserFromToken(data.accessToken);
      return data;
    } catch (error) {
      clearAuth();
      markSessionExpired();
      throw error;
    } finally {
      inflightRefresh = null;
    }
  })();

  return inflightRefresh;
}

export async function logout(): Promise<void> {
  const currentRefresh = getRefreshToken();
  try {
    if (currentRefresh) {
      await apiClient.post('/auth/logout', { refreshToken: currentRefresh });
    }
  } catch {
    // Ignore logout API errors — clear local state regardless
  } finally {
    clearAuth();
  }
}

export function clearAuth(): void {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(REFRESH_KEY);
  localStorage.removeItem(USER_KEY);
}

function setTokens(accessToken: string, refreshTokenValue: string): void {
  localStorage.setItem(TOKEN_KEY, accessToken);
  localStorage.setItem(REFRESH_KEY, refreshTokenValue);
}
